import { ApiError } from '@ghp/api-client'
import { Button, Field, Input, TextLink } from '@ghp/ui'
import { useQuery } from '@tanstack/react-query'
import { createRoute, Link } from '@tanstack/react-router'
import { Mail, User } from 'lucide-react'
import { useId, useState } from 'react'

import { rootRoute } from '../../../app/routes/root'
import { AuthLayout } from '../components/auth-layout'
import s from './login.module.css'

/**
 * « Mot de passe oublié » (D3) : la personne donne son identifiant, un lien part à l'adresse de son
 * compte. **Hors coquille**, comme la connexion : personne n'est connecté ici.
 *
 * ⚠️ La réponse est **la même** que le compte existe, n'ait pas d'adresse ou soit fermé : l'écran
 * ne doit pas apprendre à quelqu'un qu'un identifiant existe. D'où la phrase au conditionnel.
 *
 * L'envoi est coupé par défaut, et un administrateur l'allume dans « Réglages » : coupé, le même
 * formulaire **prévient le bureau**, qui renvoie l'accès depuis « Utilisateurs & rôles » (#627).
 * Rien ne s'affiche tant que la lecture n'a pas répondu, sinon le texte changerait sous les yeux.
 * ponytail: si elle échoue, l'écran parle d'e-mail ; l'API prévient le bureau quand c'est coupé.
 */
export const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordScreen,
})

function failureFor(thrown: unknown): string {
  if (thrown instanceof ApiError && thrown.status === 429) {
    return 'Trop de demandes — patienter quelques minutes avant de réessayer.'
  }
  if (thrown instanceof ApiError) {
    return `Le serveur a répondu une erreur (${String(thrown.status)}). Réessayer, puis prévenir l’équipe technique.`
  }
  return 'Serveur injoignable — vérifier la connexion et réessayer.'
}

function ForgotPasswordScreen() {
  const { api } = forgotPasswordRoute.useRouteContext()
  const failureId = useId()
  const [login, setLogin] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [sent, setSent] = useState(false)
  const availability = useQuery({
    queryKey: ['password-reset', 'availability'],
    queryFn: () => api.auth.passwordResetAvailability(),
    retry: false,
  })

  async function submit(): Promise<void> {
    // Arrêtée avant le réseau : la route ne prend que cinq demandes par minute et par adresse.
    if (login.trim() === '') {
      setFailure('Identifiant obligatoire.')
      return
    }

    setFailure(null)
    setPending(true)
    try {
      await api.auth.requestPasswordReset({ login })
      setSent(true)
    } catch (thrown) {
      setFailure(failureFor(thrown))
    } finally {
      setPending(false)
    }
  }

  const byOffice = availability.data?.enabled === false

  return (
    <AuthLayout title="Mot de passe oublié">
      {availability.isPending ? null : sent && byOffice ? (
        <p role="status">
          Demande transmise au bureau : un administrateur vous communiquera un nouveau mot de passe.
        </p>
      ) : sent ? (
        <p role="status">
          Si ce compte a une adresse e-mail, le lien vient d’y partir : il est valable 30 minutes.
          Sans adresse enregistrée, un administrateur réinitialise le mot de passe depuis «
          Utilisateurs & rôles ».
        </p>
      ) : (
        <form
          className={s.form}
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          <p>
            {byOffice
              ? 'Saisissez votre identifiant : le bureau est prévenu et vous communique un nouveau mot de passe.'
              : 'Saisissez votre identifiant : un lien pour obtenir un nouveau mot de passe part à l’adresse e-mail de votre compte.'}
          </p>
          <Field label="Identifiant">
            {({ id }) => (
              <Input
                id={id}
                name="login"
                autoComplete="username"
                autoCapitalize="none"
                icon={<User size={18} aria-hidden />}
                value={login}
                onValueChange={setLogin}
                invalid={failure === 'Identifiant obligatoire.'}
                aria-describedby={failure === null ? undefined : failureId}
              />
            )}
          </Field>
          <Button tone="brand" type="submit" fullWidth className={s.submit} disabled={pending}>
            <Mail size={18} aria-hidden />
            {byOffice ? 'Prévenir le bureau' : 'Recevoir le lien'}
          </Button>
        </form>
      )}

      {failure !== null && (
        <p id={failureId} className={s.error} role="alert">
          {failure}
        </p>
      )}

      <TextLink render={<Link to="/login" />}>Retour à la connexion</TextLink>
    </AuthLayout>
  )
}
